/// <amd-module name="VSS/Features/AvatarImagePicker/Components/AvatarImagePicker/AvatarImagePicker" />
import * as React from "react";
import Resources = require("VSS/Features/AvatarImagePicker/Resources");

import { FormatComponent } from "VSS/Platform/Components/Format";
import { VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { Button } from "VSSUI/Button";
import { Dialog } from "VSSUI/Dialog";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { KeyCode } from "VSSUI/Util";
import { IVssPersonaProps, VssPersona } from "VSSUI/VssPersona";

import { IAvatarImagePickerProps, ImageData } from "../AvatarImagePicker/AvatarImagePicker.Props";
import { FilePickerInput } from "../FilePicker/FilePickerInput";

interface IAvatarImagePickerState {
    imageChanged: boolean;
    error?: string;
    previewSelectedImage?: boolean;
    updateInProgress?: boolean;
    resetClicked?: boolean;
}

export class AvatarImagePicker extends VssComponent<IAvatarImagePickerProps, IAvatarImagePickerState> {
    private filePickerInput: FilePickerInput;
    private chooseImageButton: Button;

    private validImageTypes: string[];
    private avatarPersona: IVssPersonaProps;
    private defaultResetAvatarUrl: string;
    private selectedImageData: ImageData;

    public readonly maxFileSize: number = 2.5 * 1024 * 1024; // 2.5 MB

    constructor(props: IAvatarImagePickerProps, context?: any) {
        super(props, context);

        this.defaultResetAvatarUrl =
            this.props.defaultResetAvatarUrl ||
            (!this.props.showInitialsOnImageError
                ? (() => {
                      return getAssetLocation(this.context.pageContext, "ms.vss-features.ui-avatar-image-picker-content/content/team.svg");
                  })()
                : "");

        this.validImageTypes = this.props.validImageFormats || [".jpg", ".jpeg", ".png"];

        this.state = {
            imageChanged: false,
            updateInProgress: false,
            previewSelectedImage: false,
            error: "",
            resetClicked: false
        };
    }

    /**
     * @override
     */
    public render(): JSX.Element {
        return (
            <Dialog
                titleProps={{ text: this.props.title || Resources.ProfileImageLabel }}
                footerButtonProps={[
                    {
                        text: Resources.Cancel,
                        onClick: () => {
                            this.props.onCancel(this.state.previewSelectedImage || this.state.resetClicked);
                        }
                    },
                    {
                        text: Resources.SaveButton,
                        disabled: !this.state.previewSelectedImage && !this.state.resetClicked,
                        onClick: this._onSave,
                        primary: true
                    }
                ]}
                onDismiss={() => {
                    this.props.onCancel(this.state.previewSelectedImage || this.state.resetClicked);
                }}
                className="avatar-image-picker-dialog"
            >
                <div>
                    <div className="error-section">
                        {this.state.error ? (
                            <MessageBar
                                severity={MessageBarSeverity.Error}
                                buttonProps={[
                                    {
                                        onClick: event => {
                                            this._onDismissButtonClick(event);
                                        },
                                        iconProps: { iconName: "Cancel" },
                                        className: "dismiss-button"
                                    }
                                ]}
                            >
                                {this.state.error}
                            </MessageBar>
                        ) : null}
                    </div>
                    <div className="avatar-section flex">
                        <div className="avatar-image-section">
                            {this.state.updateInProgress ? (
                                <React.Fragment>
                                    <VssPersona {...this.avatarPersona} cssClass="avatar-persona" size="extra-large" />
                                    <div className="overlay" />
                                    <Spinner size={SpinnerSize.medium} className="spinner" />
                                </React.Fragment>
                            ) : (
                                <VssPersona {...this._getPersona()} cssClass="avatar-persona" size="extra-large" />
                            )}
                        </div>

                        <div className="avatar-input-section">
                            <div className="avatar-input-title">{Resources.SelectImageLabel}</div>
                            <Button
                                text={Resources.ChooseImage}
                                onClick={this._inputAction}
                                ref={ref => {
                                    this.chooseImageButton = ref;
                                }}
                            />

                            <FilePickerInput
                                ref={ref => {
                                    this.filePickerInput = ref;
                                }}
                                maximumFileSize={this.maxFileSize}
                                allowedFileExtensions={this.validImageTypes}
                                onload={(fileDataUrl, fileByteArray, error) => {
                                    this._onFileSelected(fileDataUrl, fileByteArray, error);
                                }}
                            />
                        </div>
                    </div>
                    <Link
                        className="reset-link fontSizeS"
                        href="#"
                        rel="noopener noreferrer"
                        aria-label={Resources.ResetLabel}
                        onClick={this._onResetClick}
                        role="button"
                    >
                        {Resources.ResetLabel}
                    </Link>

                    <div className="legal-section">
                        <Icon iconName="Info" className="ms-font-s info-icon info-icon" />
                        <FormatComponent format={Resources.Agreement} elementType="div">
                            <Link target="_blank" rel="noopener noreferrer" href="https://go.microsoft.com/fwlink/?LinkID=266231">
                                {Resources.TermsOfService}
                            </Link>
                            <Link target="_blank" rel="noopener noreferrer" href="https://go.microsoft.com/fwlink/?LinkID=264782">
                                {Resources.PrivacyStatement}
                            </Link>
                            <Link target="_blank" rel="noopener noreferrer" href="https://aka.ms/vstscodeofconduct">
                                {Resources.CodeOfConduct}
                            </Link>
                        </FormatComponent>
                    </div>
                </div>
            </Dialog>
        );
    }

    private async _onFileSelected(fileDataUrl: string, fileByteArray: number[], error: string) {
        this.selectedImageData = { url: fileDataUrl, byteArray: fileByteArray };
        this.setState({ updateInProgress: true, resetClicked: false });
        const result = await this.props.onFileSelected(error, this.selectedImageData);
        this.setState({ updateInProgress: false, previewSelectedImage: result.previewImage, error: result.error });
    }

    private _onResetClick = (): void => {
        this.selectedImageData = { url: "", byteArray: [] };
        this.setState({ resetClicked: true, error: "" });
    };

    private _onSave = (): void => {
        this.props.onSave(this.state.resetClicked, this.selectedImageData);
    };

    private _inputAction = (): void => {
        this.filePickerInput.input.click();
    };

    private _getPersona(): IVssPersonaProps {
        const imageURL = this.state.resetClicked
            ? this.defaultResetAvatarUrl
            : this.state.previewSelectedImage && !this.state.error
            ? this.selectedImageData.url
            : this.props.onLoadDisplayAvatarUrl;

        this.avatarPersona = {
            suppressPersonaCard: true,
            displayName: this.props.displayName,
            showInitialsOnImageError: this.props.showInitialsOnImageError
        };

        if (imageURL) {
            (this.avatarPersona.imageUrl = imageURL), (this.avatarPersona.imgAltText = Resources.ImageAlt);
        }

        return this.avatarPersona;
    }

    private _onDismissButtonClick = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
        this.setState({ error: undefined });
        if ((event as React.KeyboardEvent<HTMLSpanElement>).keyCode === KeyCode.enter) {
            this.chooseImageButton.focus();
        }
    };
}

VssComponent.register("vss.avatar-image-picker", AvatarImagePicker);
